import React, { useEffect, useState } from 'react';

import { LinearProgress } from '@coachhubio/ui-components';
import { useUser } from 'lib/auth';
import { useFlags } from 'lib/featureFlags/hooks/useFlags';
import { getI18n, useTranslation } from 'react-i18next';
import { fetchApi } from 'utils/fetchApi';

import { SIframe, useStyles } from './LookerDashboard.styles';
import { Title } from '../../../components/Title';
import { LookerDashboardError } from '../components/LookerDashboardError';

import { useFetchOrganizationByAccount } from '~/features/organizations/hooks/useFetchOrganizationByAccount';
import { Tenant } from '~/utils/tenants';



export function LookerDashboard({ dashId, iframeHeight }: { dashId: number; iframeHeight?: number }) {
  const { t } = useTranslation('Dashboard');
  const classes = useStyles({});
  const [lookerUrl, setLookerUrl] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [orgData, setOrganization] = useState<any>(undefined);
  const [error, setError] = useState<boolean>(false);
  const { showDashboardSections } = useFlags();
  const user = useUser();
  const language = getI18n().language;
  const { fetchOrganizationByAccount } = useFetchOrganizationByAccount();

  useEffect(() => {
    const fetchOrg = async () => {      
      try {
      const organization = await fetchOrganizationByAccount(user.accountId, user.tenantId as Tenant);

      setOrganization(organization);
    } catch (err) {
      console.error('Could not get organization data', err);
    }
    };

    fetchOrg();
  }, [user.accountId, user.tenantId]);

  useEffect(() => {
    const method = showDashboardSections ? 'lookerDashboardUrlById' : 'lookerDashboardUrl';

    // Make sure to only perform state updates on a mounted component, see: https://www.debuggr.io/react-update-unmounted-component/
    let mounted = true;
    (async () => {
      let data: { url: string };

      if (mounted) setLoading(true);

      try {
        const result = await fetchApi(`/api/${method}?lang=${language}${showDashboardSections && `&id=${dashId}`}`, {
          method: 'GET',
        });

        data = await result.json();

        if (mounted) setLookerUrl(data.url);
      } catch (err) {
        console.error('Could not get looker url', err);
        if (mounted) setError(true);
      }

      if (mounted) setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [user.organizationId, dashId, language, showDashboardSections]);

  return (
    <div className={`${classes.wrapper} ${showDashboardSections ? '' : classes.wrapperScaled}`}>
      <Title data-testid="head-section" className={`${classes.iframePadding} ${classes.title}`}>
        {!showDashboardSections && t('title')}
        {showDashboardSections && orgData && <span data-hj-suppress>{`${orgData.name}`}</span>}
        {showDashboardSections && orgData && orgData.logo && (
          <img src={orgData.logo} alt={`${orgData.name} logo`} className={classes.logo} data-hj-suppress />
        )}
      </Title>
      <div className={`${showDashboardSections ? classes.containerScaled : classes.container}`}>
        {(loading || !orgData) && (
          <div className={classes.iframePadding}>
            <LinearProgress />
          </div>
        )}
        {!loading && error && <LookerDashboardError></LookerDashboardError>}
        {!loading && !error && lookerUrl && <SIframe height={`${iframeHeight}`} url={lookerUrl}></SIframe>}
      </div>
    </div>
  );
}
